/** Pitching sim rows from `/lineup/pitching/team/:id` (`rotation.simEvents`). */

export type PitcherSimEvent = {
  roster_player_id?: number | string;
  mlb_id: number | string;
  unused_bf_total?: number | string;
  dom_play_totals?: Record<string, number>;
  games_played?: number | string;
  games_started?: number | string;
  unused_outs_total?: number | string;
};

export const formatInningsFromOuts = (outsValue: number | string | undefined | null): string => {
  const outs = Number(outsValue) || 0;
  if (outs <= 0) return '';
  const wholeInnings = Math.floor(outs / 3);
  const remainder = outs % 3;
  return remainder === 0 ? String(wholeInnings) : `${wholeInnings}.${remainder}`;
};

export const buildPitcherSimMap = (events: PitcherSimEvent[]): Record<string, PitcherSimEvent> => {
  const map: Record<string, PitcherSimEvent> = {};
  (events || []).forEach((ev) => {
    if (!ev) return;
    if (ev.roster_player_id != null) {
      map[`rp:${ev.roster_player_id}`] = ev;
    }
    if (ev.mlb_id != null) {
      map[String(ev.mlb_id)] = ev;
    }
  });
  return map;
};

export const getPitcherLW = (sim: PitcherSimEvent | undefined): number | null => {
  if (!sim || !sim.dom_play_totals) return null;
  const totals = sim.dom_play_totals;
  const get = (key: string): number => Number(totals[key] ?? 0) || 0;
  const outs = Number(sim.unused_outs_total ?? 0) || 0;
  const oneB = get('1B');
  const twoB = get('2B');
  const threeB = get('3B');
  const hr = get('HR');
  const bb = get('BB') + get('IBB');
  const hbp = get('HBP');
  const sb = get('SB');
  const cs = get('CS');
  const lw =
    0.28 * outs -
    0.47 * oneB -
    0.78 * twoB -
    1.09 * threeB -
    1.4 * hr -
    0.33 * bb -
    0.34 * hbp -
    0.2 * sb +
    0.4 * cs;
  return lw;
};

export const attachSimStats = (pitcher: any, simMap: Record<string, PitcherSimEvent>) => {
  const byRp =
    pitcher?.roster_player_id != null ? simMap[`rp:${pitcher.roster_player_id}`] : undefined;
  const mlbKey = pitcher?.mlb_id != null ? String(pitcher.mlb_id) : null;
  const sim = byRp ?? (mlbKey ? simMap[mlbKey] : undefined);
  if (!sim) return pitcher;
  const gamesPlayed = Number(sim.games_played ?? 0);
  const gamesStarted = Number(sim.games_started ?? 0);
  const ip = formatInningsFromOuts(sim.unused_outs_total);
  const lw = getPitcherLW(sim);
  const stats = {
    ...(pitcher.stats || {}),
    G: gamesPlayed || undefined,
    GS: gamesStarted || undefined,
    IP: ip || undefined,
    LW: lw != null ? lw.toFixed(2) : undefined,
  };
  return { ...pitcher, stats };
};
